import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, Mic, FileText, Dumbbell, Trophy, Clock, CheckCircle2, Circle, Bell, X } from "lucide-react";
import api from "../../lib/api";
import { PausedWinBack } from "./PausedWinBack";

const ago = (iso) => { const d = (Date.now() - new Date(iso).getTime()) / 864e5; return d < 1 ? "today" : d < 2 ? "yesterday" : `${Math.floor(d)}d ago`; };

const VISITED_KEY = "fireprep-visited";
export const markVisited = (key) => { try { const v = JSON.parse(localStorage.getItem(VISITED_KEY) || "{}"); if (!v[key]) { v[key] = 1; localStorage.setItem(VISITED_KEY, JSON.stringify(v)); } } catch { /* ignore */ } };
const visited = () => { try { return JSON.parse(localStorage.getItem(VISITED_KEY) || "{}"); } catch { return {}; } };

// Starting-point strip: each step disappears once the user has started it (server data) or opened that area (local visit mark).
function Welcome({ name, started }) {
  const navigate = useNavigate();
  const v = visited();
  const STEPS = [["study", "Take your first practice questions", "/study", BookOpen], ["interview", "Try a mock oral board", "/interview", Mic], ["fitness", "Set your fitness goals", "/fitness", Dumbbell], ["resume", "Start your resume", "/resume", FileText]];
  const items = STEPS.filter(([k]) => !started?.[k] && !v[k]);
  if (!items.length) return null;
  const go = (k, to) => { markVisited(k); if (to.includes("#")) document.getElementById("roadmap")?.scrollIntoView({ behavior: "smooth" }); else navigate(to); };
  return (
    <section className="bg-slate-950 text-white rounded-xl px-4 py-3 sm:px-5" data-testid="dashboard-welcome">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <h2 className="font-heading text-lg">Getting started</h2>
        <p className="text-slate-400 text-xs">{items.length === STEPS.length ? "Pick a starting point — the dashboard fills in as you go." : "Still to try:"}</p>
      </div>
      <ul className="mt-2 flex flex-wrap gap-1.5">
        {items.map(([k, t, to, Icon], i) => (
          <li key={k}><button type="button" onClick={() => go(k, to)} className="inline-flex items-center gap-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 pl-1 pr-3 py-1 text-left text-xs font-semibold" data-testid={`welcome-step-${k}`}><span className="w-5 h-5 rounded-full bg-amber-400 text-slate-950 text-[10px] font-bold flex items-center justify-center">{i + 1}</span><Icon className="w-3.5 h-3.5 text-amber-400" /> {t}</button></li>
        ))}
      </ul>
    </section>
  );
}

function Alerts() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/notifications").then(({ data }) => setItems(data.items)).catch(() => {}); }, []);
  if (!items.length) return null;
  const dismiss = (id) => { api.post(`/notifications/${id}/read`).catch(() => {}); setItems((x) => x.filter((n) => n.id !== id)); };
  return (
    <section className="space-y-2" data-testid="dashboard-alerts">
      {items.map((n) => (
        <div key={n.id} className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3" data-testid={`alert-${n.id}`}>
          <Bell className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-amber-900">{n.title}</p><p className="text-xs text-amber-800 mt-0.5">{n.body}</p>
            {n.link && <button type="button" onClick={() => { dismiss(n.id); navigate(n.link); }} className="mt-1 text-xs font-semibold text-amber-900 underline" data-testid={`alert-open-${n.id}`}>Open profile</button>}</div>
          <button type="button" onClick={() => dismiss(n.id)} className="text-amber-700 hover:text-amber-950 p-1" aria-label="Dismiss" data-testid={`alert-dismiss-${n.id}`}><X className="w-4 h-4" /></button>
        </div>
      ))}
    </section>
  );
}

export function DashboardHub({ roadmap = null }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard/overview").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return null;
  return (
    <div className="space-y-6" data-testid="dashboard-hub">
      <Welcome name={d.name} started={d.started} />
      <PausedWinBack />
      {d.board?.last_score != null && (
        <button type="button" onClick={() => navigate("/interview?tab=results")} className="w-full flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-left hover:border-slate-300" data-testid="dashboard-last-board">
          <span className="flex items-center gap-3 text-sm text-slate-700"><Mic className="w-4 h-4 text-primary" /> Latest Mock Oral Board <span className={`font-heading text-xl ${d.board.last_score >= 70 ? "text-green-600" : d.board.last_score >= 50 ? "text-amber-600" : "text-red-600"}`}>{d.board.last_score}</span><span className="text-xs text-slate-400">/ 100 · {d.board.completed} graded</span></span>
          <span className="text-xs font-semibold text-primary">View results →</span>
        </button>
      )}
      {!d.is_new && <Alerts />}

      {/* MAIN: Career Road Map */}
      <div id="roadmap">{roadmap}</div>

    </div>
  );
}

export function DashboardExtras() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard/overview").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d || d.is_new) return null;
  const go = (to) => (to.includes("#") ? document.getElementById("roadmap")?.scrollIntoView({ behavior: "smooth" }) : navigate(to));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-8" data-testid="dashboard-extras">
      <section className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="recent-activity">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-primary" /> Recent activity</p>
        {d.activity.length ? <ul className="mt-3 space-y-2">{d.activity.slice(0, 6).map((a, i) => <li key={i}><button type="button" onClick={() => go(a.to)} className="w-full text-left text-sm text-slate-700 hover:text-primary flex items-start justify-between gap-2"><span>{a.text}</span><span className="text-[11px] text-slate-400 shrink-0">{ago(a.at)}</span></button></li>)}</ul> : <p className="mt-3 text-sm text-slate-500">Your activity will show up here.</p>}
      </section>
      <section className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="achievements">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5 text-primary" /> Milestones · {d.achievements.filter((a) => a.earned).length}/{d.achievements.length}</p>
        <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1">{d.achievements.map((a) => <li key={a.id} className={`flex items-center gap-2 text-sm ${a.earned ? "text-slate-800" : "text-slate-400"}`} data-testid={`achievement-${a.id}`}>{a.earned ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Circle className="w-4 h-4" />}{a.title}</li>)}</ul>
      </section>
    </div>
  );
}
